import React, { useEffect, useRef, useState } from "react";
import {
  animate,
  useInView,
  useMotionValue,
  useReducedMotion,
} from "framer-motion";

const DEFAULT_FORMAT = (n) =>
  Math.round(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

/**
 * Animated number counter.
 *
 *   <Counter to={2_500_000} format={(n) => "$" + n.toLocaleString() + "M+"} />
 *
 * Reduced-motion: shows the final value immediately without any animation.
 */
export function Counter({
  to,
  duration = 1200,
  format = DEFAULT_FORMAT,
  startOnView = true,
  from = 0,
  className,
  ...rest
}) {
  const target = Number(to);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const reduce = useReducedMotion();
  const mv = useMotionValue(reduce ? target : from);
  const [display, setDisplay] = useState(() => format(reduce ? target : from));

  // Subscribe to motion value changes
  useEffect(() => {
    return mv.on("change", (v) => setDisplay(format(v)));
  }, [mv, format]);

  // Kick off animation when in view (or immediately if disabled)
  useEffect(() => {
    if (reduce) {
      mv.set(target);
      setDisplay(format(target));
      return;
    }
    if (startOnView && !inView) return;
    const controls = animate(mv, target, {
      duration: duration / 1000,
      ease: [0.2, 0.8, 0.2, 1],
    });
    return () => controls.stop();
  }, [inView, startOnView, target, duration, reduce, mv, format]);

  return (
    <span ref={ref} className={className} {...rest}>
      {display}
    </span>
  );
}

export default Counter;
